import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowsClockwise,
  Buildings,
  Calendar,
  Users,
  Trash,
  X,
} from "@phosphor-icons/react";
import ConfirmDialog from "./ConfirmDialog";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPE_LABELS = {
  board_and_care: "Board & Care",
  assisted_living: "Assisted Living / Senior Living",
  hospice: "Hospice",
  hospital: "Hospital / Healthcare",
  church: "Church / Faith Community",
  business: "Business / Employer",
  community: "Community Organization",
  other: "Other",
};

const PROGRAM_LABELS = {
  from_diagnosis_to_goodbye_presentation: "From Diagnosis to Goodbye Presentation",
  hospice_family_education: "Hospice Family Education",
  grief_bereavement_support: "Grief & Bereavement Support",
  caregiver_support: "Caregiver Support",
  staff_grief_support: "Staff Grief Support",
  compassion_fatigue_burnout: "Compassion Fatigue / Burnout",
  devotional_music: "Devotional & Music",
  workshop_training: "Workshop / Training",
  speaking_engagement: "Speaking Engagement",
  other: "Other",
};

const FORMAT_LABELS = {
  in_person: "In Person",
  online: "Online",
  either: "Either",
};

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-800" },
  { value: "pending", label: "Pending", color: "bg-purple-100 text-purple-800" },
  { value: "confirmed", label: "Confirmed", color: "bg-emerald-100 text-emerald-800" },
  { value: "completed", label: "Completed", color: "bg-primary/15 text-primary" },
  { value: "declined", label: "Declined", color: "bg-red-100 text-red-800" },
  { value: "cancelled", label: "Cancelled", color: "bg-ink-100 text-ink-600" },
];

const statusPill = (value) => {
  const opt = STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${opt.color}`}>
      {opt.label}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};
const fmtTime = (t) => {
  if (!t) return "—";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh || "0", 10);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm || "00"} ${suffix}`;
};

const OrganizationBookingsAdmin = ({ onDataChange }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [orgType, setOrgType] = useState("all");
  const [program, setProgram] = useState("all");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refreshStats = useCallback(() => {
    axios
      .get(`${API_BASE}/admin/organization-bookings/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (orgType !== "all") params.org_type = orgType;
      if (program !== "all") params.program = program;
      if (status !== "all") params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (q.trim()) params.q = q.trim();
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/organization-bookings`, { params, withCredentials: true }),
        axios.get(`${API_BASE}/admin/organization-bookings/stats`, { withCredentials: true }),
      ]);
      setItems(listRes.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error("Could not load organization bookings.");
    } finally {
      setLoading(false);
    }
  }, [orgType, program, status, fromDate, toDate, q]);

  useEffect(() => {
    load();
  }, [orgType, program, status, fromDate, toDate, load]);

  // Prune selections that are no longer visible so we never silently
  // hold ids off-screen.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(items.map((i) => i.id));
      const next = new Set();
      prev.forEach((id) => visible.has(id) && next.add(id));
      return next;
    });
  }, [items]);

  const updateSelected = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_BASE}/admin/organization-bookings/${selected.id}`,
        patch,
        { withCredentials: true }
      );
      setSelected(res.data);
      setItems((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
      refreshStats();
      onDataChange?.();
      toast.success("Updated.");
    } catch {
      toast.error("Could not save the change.");
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected =
    items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      clearSelection();
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const askDeleteSingle = (id) =>
    setConfirmDelete({ mode: "single", ids: [id] });
  const askDeleteBulk = () => {
    if (selectedIds.size === 0) return;
    setConfirmDelete({ mode: "bulk", ids: Array.from(selectedIds) });
  };
  const cancelDelete = () => setConfirmDelete(null);

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { mode, ids } = confirmDelete;
    setDeleting(true);
    try {
      if (mode === "single") {
        await axios.delete(
          `${API_BASE}/admin/organization-bookings/${ids[0]}`,
          { withCredentials: true }
        );
        setItems((prev) => prev.filter((r) => r.id !== ids[0]));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(ids[0]);
          return next;
        });
        if (selected?.id === ids[0]) setSelected(null);
        toast.success("Booking request deleted");
      } else {
        const res = await axios.post(
          `${API_BASE}/admin/organization-bookings/bulk-delete`,
          { ids },
          { withCredentials: true }
        );
        const removed = new Set(ids);
        setItems((prev) => prev.filter((r) => !removed.has(r.id)));
        clearSelection();
        const n = res.data?.deleted ?? ids.length;
        toast.success(`${n} booking request${n === 1 ? "" : "s"} deleted`);
      }
      refreshStats();
      onDataChange?.();
      setConfirmDelete(null);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session ended. Please sign in again.");
      } else if (err?.response?.status === 404) {
        toast.error("Record no longer exists. Refreshing list…");
        load();
        setConfirmDelete(null);
      } else {
        toast.error("Could not delete. Please try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const totals = useMemo(
    () => ({
      total: stats?.total || 0,
      new: stats?.by_status?.new || 0,
      pending: stats?.by_status?.pending || 0,
      confirmed: stats?.by_status?.confirmed || 0,
      upcoming: stats?.upcoming_confirmed || 0,
    }),
    [stats]
  );

  return (
    <section data-testid="organization-bookings-section" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-medium text-ink-900">
            Organization Bookings
          </h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Presentations, workshops, programs, trainings, and speaking engagements.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="ob-admin-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-primary hover:text-primary"
        >
          <ArrowsClockwise size={12} weight="bold" />
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Buildings} label="Total requests" value={totals.total} testid="ob-stat-total" />
        <StatCard icon={Calendar} label="New" value={totals.new} accent testid="ob-stat-new" />
        <StatCard icon={Users} label="Pending" value={totals.pending} testid="ob-stat-pending" />
        <StatCard icon={Users} label="Confirmed" value={totals.confirmed} testid="ob-stat-confirmed" />
        <StatCard icon={Calendar} label="Upcoming confirmed" value={totals.upcoming} testid="ob-stat-upcoming" />
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-ink-100 bg-white p-3 shadow-sm flex flex-wrap items-center gap-3">
        <select
          value={orgType}
          onChange={(e) => setOrgType(e.target.value)}
          data-testid="ob-filter-org-type"
          className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 focus:border-primary outline-none"
        >
          <option value="all">All organizations</option>
          {Object.entries(ORG_TYPE_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={program}
          onChange={(e) => setProgram(e.target.value)}
          data-testid="ob-filter-program"
          className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 focus:border-primary outline-none"
        >
          <option value="all">All programs</option>
          {Object.entries(PROGRAM_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="ob-filter-status"
          className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 focus:border-primary outline-none"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="ob-filter-from"
          className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-700 focus:border-primary outline-none"
          title="From requested date"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="ob-filter-to"
          className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs text-ink-700 focus:border-primary outline-none"
          title="To requested date"
        />
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex-1 min-w-[240px] flex items-center gap-2"
        >
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search organization, contact, or email…"
              data-testid="ob-search-input"
              className="w-full rounded-full border border-ink-200 pl-8 pr-3 py-1.5 text-xs focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
            />
          </div>
          <button
            type="submit"
            data-testid="ob-search-submit"
            className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark"
          >
            Search
          </button>
        </form>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50/70 px-4 py-2.5"
          data-testid="ob-bulk-bar"
        >
          <div className="text-sm text-ink-800">
            <strong data-testid="ob-bulk-count">{selectedIds.size}</strong>{" "}
            selected
            <span className="text-ink-500"> (of {items.length} shown)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              data-testid="ob-bulk-clear"
              className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50"
            >
              Clear selection
            </button>
            <button
              type="button"
              onClick={askDeleteBulk}
              data-testid="ob-bulk-delete"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
            >
              <Trash size={12} weight="bold" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-ink-100 bg-white overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="ob-table">
          <thead className="bg-softblue/40 text-ink-700">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible bookings"
                  data-testid="ob-select-all"
                  className="h-4 w-4 accent-primary cursor-pointer"
                />
              </th>
              <Th>Submitted</Th>
              <Th>Organization</Th>
              <Th>Type</Th>
              <Th>Contact</Th>
              <Th>Program</Th>
              <Th>Preferred date</Th>
              <Th>Time</Th>
              <Th>Format</Th>
              <Th>Est.</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-8 text-center text-ink-500 text-sm">
                  {loading ? "Loading..." : "No requests match the current filters."}
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`ob-row-${r.id}`}
                  onClick={() => setSelected(r)}
                  className={`border-t border-ink-100 hover:bg-softblue/20 cursor-pointer ${
                    selectedIds.has(r.id) ? "bg-red-50/40" : ""
                  }`}
                >
                  <td
                    className="px-3 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                      aria-label={`Select booking from ${r.organization_name}`}
                      data-testid={`ob-select-${r.id}`}
                      className="h-4 w-4 accent-primary cursor-pointer"
                    />
                  </td>
                  <Td className="text-ink-600 text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</Td>
                  <Td className="font-medium text-ink-900">{r.organization_name}</Td>
                  <Td className="text-ink-700 text-xs">{ORG_TYPE_LABELS[r.organization_type] || r.organization_type}</Td>
                  <Td className="text-ink-700 text-xs">
                    <div>{r.contact_first_name} {r.contact_last_name}</div>
                    <div className="text-ink-500">{r.email}</div>
                  </Td>
                  <Td className="text-ink-700 text-xs">{PROGRAM_LABELS[r.program] || r.program}</Td>
                  <Td className="text-ink-700 text-xs whitespace-nowrap">{fmtDate(r.preferred_date)}</Td>
                  <Td className="text-ink-700 text-xs whitespace-nowrap">{fmtTime(r.preferred_start_time)}</Td>
                  <Td className="text-ink-700 text-xs">{FORMAT_LABELS[r.format] || r.format}</Td>
                  <Td className="text-ink-700 text-xs">{r.estimated_attendance ?? "—"}</Td>
                  <Td>{statusPill(r.status)}</Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <DetailDrawer
          booking={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateSelected}
          onDelete={() => askDeleteSingle(selected.id)}
          saving={saving}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={
          confirmDelete?.mode === "bulk"
            ? `Delete ${confirmDelete.ids.length} selected booking request${
                confirmDelete.ids.length === 1 ? "" : "s"
              }?`
            : "Delete this booking request?"
        }
        description={
          confirmDelete?.mode === "bulk"
            ? `This will permanently remove ${confirmDelete.ids.length} booking request${
                confirmDelete.ids.length === 1 ? "" : "s"
              } and cannot be undone.`
            : "This will permanently remove this booking request. This action cannot be undone."
        }
        confirmLabel={
          confirmDelete?.mode === "bulk"
            ? `Delete ${confirmDelete.ids.length} booking${
                confirmDelete.ids.length === 1 ? "" : "s"
              }`
            : "Delete Booking"
        }
        busy={deleting}
        onConfirm={doDelete}
        onCancel={cancelDelete}
        testId="ob-confirm-delete"
      />
    </section>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl border p-4 shadow-sm ${accent ? "border-primary/30 bg-primary/5" : "border-ink-100 bg-white"}`}
  >
    <div className="flex items-center gap-2 text-ink-500">
      <Icon size={14} weight="duotone" className="text-primary" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
        {label}
      </span>
    </div>
    <div className="mt-2 font-heading text-2xl text-ink-900 font-medium">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="text-left text-[10px] font-semibold uppercase tracking-[0.18em] px-4 py-3">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

const DetailDrawer = ({ booking, onClose, onUpdate, onDelete, saving }) => {
  const [notes, setNotes] = useState(booking.internal_notes || "");
  const [confDate, setConfDate] = useState(booking.confirmed_date || "");
  const [confStart, setConfStart] = useState(booking.confirmed_start_time?.slice(0, 5) || "");
  const [confEnd, setConfEnd] = useState(booking.confirmed_end_time?.slice(0, 5) || "");
  const [confLoc, setConfLoc] = useState(booking.confirmed_location || "");
  const [confLink, setConfLink] = useState(booking.online_meeting_link || "");

  useEffect(() => {
    setNotes(booking.internal_notes || "");
    setConfDate(booking.confirmed_date || "");
    setConfStart(booking.confirmed_start_time?.slice(0, 5) || "");
    setConfEnd(booking.confirmed_end_time?.slice(0, 5) || "");
    setConfLoc(booking.confirmed_location || "");
    setConfLink(booking.online_meeting_link || "");
  }, [booking.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveConfirmed = () => {
    onUpdate({
      confirmed_date: confDate || null,
      confirmed_start_time: confStart ? `${confStart}:00` : null,
      confirmed_end_time: confEnd ? `${confEnd}:00` : null,
      confirmed_location: confLoc || null,
      online_meeting_link: confLink || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" data-testid="ob-detail-drawer">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-start gap-3 border-b border-ink-100 bg-gradient-to-br from-primary to-primary-dark px-5 py-4 text-white">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-secondary">Booking Request</div>
            <div className="mt-0.5 font-heading text-lg font-medium">{booking.organization_name}</div>
            <div className="mt-0.5 text-xs opacity-90">{PROGRAM_LABELS[booking.program] || booking.program}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="ob-detail-close" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85 hover:bg-white/10">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          <FieldRow label="Submitted">{fmtDateTime(booking.created_at)}</FieldRow>
          <FieldRow label="Organization type">
            {ORG_TYPE_LABELS[booking.organization_type] || booking.organization_type}
            {booking.organization_type_other && <span className="text-ink-500"> — {booking.organization_type_other}</span>}
          </FieldRow>
          <FieldRow label="Contact">
            {booking.contact_first_name} {booking.contact_last_name}
            {booking.title && <span className="text-ink-500"> — {booking.title}</span>}
          </FieldRow>
          <FieldRow label="Email"><a href={`mailto:${booking.email}`} className="text-primary hover:underline">{booking.email}</a></FieldRow>
          <FieldRow label="Phone">{booking.phone || "—"}</FieldRow>
          <FieldRow label="Preferred contact">{booking.preferred_contact}</FieldRow>
          <FieldRow label="Program">
            {PROGRAM_LABELS[booking.program] || booking.program}
            {booking.program_other && <span className="text-ink-500"> — {booking.program_other}</span>}
          </FieldRow>
          <FieldRow label="Format">{FORMAT_LABELS[booking.format]}</FieldRow>
          {booking.format === "in_person" && (
            <FieldRow label="Location">
              {[booking.city, booking.state].filter(Boolean).join(", ")}
              {booking.venue_address && (
                <div className="text-ink-500 text-xs mt-0.5">{booking.venue_address}</div>
              )}
            </FieldRow>
          )}
          <FieldRow label="Preferred date/time">
            {fmtDate(booking.preferred_date)} at {fmtTime(booking.preferred_start_time)}
          </FieldRow>
          {booking.alternate_date && (
            <FieldRow label="Alternate">
              {fmtDate(booking.alternate_date)} at {fmtTime(booking.alternate_start_time)}
            </FieldRow>
          )}
          <FieldRow label="Est. attendance">{booking.estimated_attendance ?? "—"}</FieldRow>
          <FieldRow label="Audience">{booking.audience_type || "—"}</FieldRow>
          {booking.additional_info && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">Additional info</div>
              <div className="mt-1 rounded-lg bg-softblue/40 border border-ink-100 px-3 py-2 text-sm text-ink-800 whitespace-pre-wrap">
                {booking.additional_info}
              </div>
            </div>
          )}

          <div className="border-t border-ink-100 pt-4">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">Status</span>
              <select
                value={booking.status}
                onChange={(e) => onUpdate({ status: e.target.value })}
                disabled={saving}
                data-testid="ob-detail-status"
                className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Confirmed fields (visible when status is confirmed or completed) */}
          {(booking.status === "confirmed" || booking.status === "completed") && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-3" data-testid="ob-confirmed-fields">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                Confirmed booking details
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-ink-700">Confirmed date</span>
                  <input
                    type="date"
                    value={confDate}
                    onChange={(e) => setConfDate(e.target.value)}
                    data-testid="ob-detail-confirmed-date"
                    className="mt-1 block w-full rounded-lg border border-ink-200 px-2 py-1.5 text-sm outline-none focus:border-primary"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-xs font-semibold text-ink-700">Start</span>
                    <input
                      type="time"
                      value={confStart}
                      onChange={(e) => setConfStart(e.target.value)}
                      data-testid="ob-detail-confirmed-start"
                      className="mt-1 block w-full rounded-lg border border-ink-200 px-2 py-1.5 text-sm outline-none focus:border-primary"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-ink-700">End</span>
                    <input
                      type="time"
                      value={confEnd}
                      onChange={(e) => setConfEnd(e.target.value)}
                      data-testid="ob-detail-confirmed-end"
                      className="mt-1 block w-full rounded-lg border border-ink-200 px-2 py-1.5 text-sm outline-none focus:border-primary"
                    />
                  </label>
                </div>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-ink-700">Location or "Online"</span>
                <input
                  type="text"
                  value={confLoc}
                  onChange={(e) => setConfLoc(e.target.value)}
                  data-testid="ob-detail-confirmed-loc"
                  className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-ink-700">Online meeting link (optional)</span>
                <input
                  type="url"
                  value={confLink}
                  onChange={(e) => setConfLink(e.target.value)}
                  data-testid="ob-detail-confirmed-link"
                  className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </label>
              <button
                type="button"
                onClick={saveConfirmed}
                disabled={saving}
                data-testid="ob-detail-save-confirmed"
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
              >
                Save confirmed details
              </button>
              <p className="text-[11px] text-ink-500 leading-snug">
                These details are stored only. No email is sent automatically when confirmed details change.
              </p>
            </div>
          )}

          <div>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">Internal notes</span>
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="ob-detail-notes"
                className="mt-1 block w-full resize-none rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
              />
            </label>
            <button
              type="button"
              onClick={() => onUpdate({ internal_notes: notes })}
              disabled={saving}
              data-testid="ob-detail-save-notes"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
            >
              Save notes
            </button>
          </div>

          <div className="mt-4 border-t border-ink-100 pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
              Danger zone
            </div>
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              data-testid="ob-detail-delete"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash size={12} weight="bold" />
              Delete booking request
            </button>
            <p className="mt-2 text-[11px] leading-snug text-ink-500">
              Use this only for test data, duplicates, spam, or clearly unwanted submissions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const FieldRow = ({ label, children }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">{label}</div>
    <div className="mt-0.5 text-sm text-ink-800">{children}</div>
  </div>
);

export default OrganizationBookingsAdmin;
